import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

// Format currency for export - fix the currency display
const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  
  // Extract numeric value properly
  let numericAmount;
  if (typeof amount === 'string') {
    numericAmount = parseFloat(amount.replace(/[^\d.-]/g, ''));
  } else {
    numericAmount = amount;
  }
  
  if (isNaN(numericAmount)) return '₹0';
  
  // Format with proper currency symbol
  if (numericAmount >= 10000000) { // 1 Crore or more
    return `₹${(numericAmount / 10000000).toFixed(1)} Cr`;
  } else if (numericAmount >= 100000) { // 1 Lakh or more
    return `₹${(numericAmount / 100000).toFixed(1)} L`;
  } else if (numericAmount >= 1000) { // 1 Thousand or more
    return `₹${(numericAmount / 1000).toFixed(0)} K`;
  } else {
    return `₹${numericAmount.toFixed(0)}`;
  }
};

// Format date for export
const formatDate = (dateString) => {
  return dateString || '';
};

// NEW: Generate PDF by capturing the table as image (preserves Marathi text)
export const exportToPDFWithCapture = async (projects, title, selectedDepartment, selectedStatus) => {
  try {
    // Find the project table element
    const tableElement = document.getElementById('project-listing-section');
    if (!tableElement) {
      alert('Table not found. Please ensure the project table is visible.');
      return;
    }

    // Create PDF
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // Add UMC Header
    doc.setFillColor(20, 184, 166); // Teal background
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // UMC Logo
    doc.setFillColor(255, 255, 255);
    doc.circle(30, 22, 8, 'F');
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('UMC', 30, 25, { align: 'center' });
    
    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Ulhasnagar Municipal Corporation', 50, 20);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('IWMS - Project Monitoring Dashboard', 50, 28);
    
    // Title section
    const exportTitle = title || `${selectedDepartment || selectedStatus || 'All'} Projects`;
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 45, pageWidth, 20, 'F');
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(exportTitle, margin, 58);
    
    // Export info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const exportDate = new Date().toLocaleString('en-IN');
    doc.text(`Exported on: ${exportDate}`, margin, 62);
    doc.text(`Total Projects: ${projects.length}`, pageWidth - margin - 40, 62);
    
    // Capture table as image
    const canvas = await html2canvas(tableElement, {
      scale: 2, // Higher resolution
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: tableElement.offsetWidth,
      height: tableElement.offsetHeight
    });
    
    // Calculate image dimensions to fit in PDF
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pageWidth - (2 * margin);
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Check if image fits on page, if not scale it down
    const maxHeight = pageHeight - 90; // Leave space for header and footer
    let finalWidth = imgWidth;
    let finalHeight = imgHeight;
    
    if (imgHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }
    
    // Add the captured image to PDF
    doc.addImage(imgData, 'PNG', margin, 75, finalWidth, finalHeight);
    
    // Add footer
    const footerY = pageHeight - 15;
    doc.setFillColor(248, 250, 252);
    doc.rect(0, footerY - 5, pageWidth, 15, 'F');
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, footerY - 5, pageWidth, footerY - 5);
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Page 1 of 1', margin, footerY);
    doc.text('Generated by IWMS Dashboard', pageWidth - margin, footerY, { align: 'right' });
    
    // Save PDF
    const fileName = `${exportTitle.replace(/\s+/g, '_')}_Projects_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    console.log('PDF with captured table exported successfully');
  } catch (error) {
    console.error('PDF capture export error:', error);
    alert('Error generating PDF. Please try again.');
  }
};

// Generate PDF export
export const exportToPDF = (projects, title, selectedDepartment, selectedStatus) => {
  try {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    
    // Set the main theme colors
    const primaryColor = [20, 184, 166]; // Teal-500
    const secondaryColor = [59, 130, 246]; // Blue-500
    const headerColor = [15, 23, 42]; // Slate-900
    
    // Add header background
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Add UMC Logo placeholder (you can replace with actual logo)
    doc.setFillColor(255, 255, 255);
    doc.circle(30, 25, 10, 'F');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('UMC', 30, 28, { align: 'center' });
    
    // Add header text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Ulhasnagar Municipal Corporation', 50, 22);
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('IWMS - Project Monitoring Dashboard', 50, 30);
    
    // Add title section
    doc.setFillColor(248, 250, 252); // Light gray background
    doc.rect(0, 45, pageWidth, 25, 'F');
    
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2]);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const exportTitle = title || `${selectedDepartment || selectedStatus || 'All'} Projects`;
    doc.text(exportTitle, margin, 58);
    
    // Add export info
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const exportDate = new Date().toLocaleString('en-IN');
    doc.text(`Exported on: ${exportDate}`, margin, 65);
    doc.text(`Total Projects: ${projects.length}`, pageWidth - margin - 40, 65);
    
    // Prepare table data with proper text handling
    const tableHeaders = [
      'Sr.',
      'Project Name', 
      'Fund Head',
      'Amount',
      'Date',
      'Status',
      'Progress'
    ];
    
    const tableData = projects.map((project, index) => [
      String(index + 1).padStart(2, '0'),
      cleanTextForPDF(project.project_name || ''), // No truncation - show full name
      cleanTextForPDF(project.fund_head || ''),   // No truncation - show full fund head
      formatCurrency(project.ts_amount),
      formatDate(project.ts_date),
      project.status || '',
      `${project.progress || 0}%`
    ]);
    
    // Simple table with dynamic row height and proper cell wrapping
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 75,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        minCellHeight: 8
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [51, 51, 51],
        valign: 'top'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 }, // Sr. No.
        1: { cellWidth: 60, overflow: 'linebreak' }, // Project Name - wider with line break
        2: { cellWidth: 40, overflow: 'linebreak' }, // Fund Head - wider with line break
        3: { halign: 'right', cellWidth: 25 }, // TS Amount
        4: { halign: 'center', cellWidth: 18 }, // TS Date
        5: { halign: 'center', cellWidth: 18 }, // Status
        6: { halign: 'center', cellWidth: 15 } // Progress
      },
      didParseCell: function(data) {
        // Add borders to all cells
        data.cell.styles.lineColor = [200, 200, 200];
        data.cell.styles.lineWidth = 0.5;
        
        // Color code status column
        if (data.column.index === 5) { // Status column
          const status = data.cell.text[0];
          switch (status) {
            case 'Completed':
              data.cell.styles.fillColor = [220, 252, 231]; // Green-100
              data.cell.styles.textColor = [21, 128, 61]; // Green-700
              break;
            case 'In Progress':
              data.cell.styles.fillColor = [219, 234, 254]; // Blue-100
              data.cell.styles.textColor = [29, 78, 216]; // Blue-700
              break;
            case 'Delayed':
              data.cell.styles.fillColor = [254, 226, 226]; // Red-100
              data.cell.styles.textColor = [185, 28, 28]; // Red-700
              break;
          }
        }
      }
    });
    
    // Add footer with border
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer background
      const footerY = doc.internal.pageSize.getHeight() - 15;
      doc.setFillColor(248, 250, 252);
      doc.rect(0, footerY - 5, pageWidth, 15, 'F');
      
      // Footer border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(0, footerY - 5, pageWidth, footerY - 5);
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Page ${i} of ${pageCount}`,
        margin,
        footerY,
        { align: 'left' }
      );
      doc.text(
        'Generated by IWMS Dashboard',
        pageWidth - margin,
        footerY,
        { align: 'right' }
      );
    }
    
    // Save the PDF
    const fileName = `${exportTitle.replace(/\s+/g, '_')}_Projects_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    console.log('PDF export completed successfully');
  } catch (error) {
    console.error('PDF export error:', error);
    alert('Error generating PDF. Please try again.');
  }
};

// Generate Excel export
export const exportToExcel = (projects, title, selectedDepartment, selectedStatus) => {
  try {
    // Remove duplicates based on unique project ID
    const uniqueProjects = projects.filter((project, index, arr) => 
      index === arr.findIndex(p => p.id === project.id)
    );
    
    console.log(`Original projects: ${projects.length}, Unique projects: ${uniqueProjects.length}`);
    
    // Prepare data
    const exportTitle = title || `${selectedDepartment || selectedStatus || 'All'} Projects`;
    const exportDate = new Date().toLocaleString('en-IN');
    
    // Create header rows
    const headerData = [
      ['Ulhasnagar Municipal Corporation'],
      ['IWMS - Project Monitoring Dashboard'],
      [''],
      [exportTitle],
      [`Exported on: ${exportDate}`, '', '', '', '', '', '', '', '', '', `Total Projects: ${uniqueProjects.length}`],
      [''],
      ['Sr. No.', 'Project Name', 'Work Type', 'Fund Head', 'Department', 'Agency', 'TS Date', 'TS Amount', 'WO Date', 'WO No', 'WO Amount', 'Status', 'Longitude', 'Latitude']
    ];
    
    // Prepare project data using uniqueProjects and ensuring proper serial numbers
    const projectData = uniqueProjects.map((project, index) => [
      index + 1,  // Proper sequential numbering starting from 1
      project.project_name || '',
      project.work_type || 'Not Specified',
      project.fund_head || '',
      project.department || 'Not Assigned',
      project.agency || 'Not Specified',
      project.ts_sanction_date || '',
      project.recap_amt ? parseFloat(project.recap_amt.toString().replace(/[^\d.-]/g, '')) : 0,
      project.work_order_date || '',
      project.work_order_no || '',
      project.total_tender_amount ? parseFloat(project.total_tender_amount.toString().replace(/[^\d.-]/g, '')) : 0,
      project.status || '',
      project.longitude || 0,
      project.latitude || 0
    ]);
    
    // Combine all data
    const allData = [...headerData, ...projectData];
    
    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(allData);
    const wb = XLSX.utils.book_new();
    
    // Set column widths
    const colWidths = [
      { wch: 8 },  // Sr. No.
      { wch: 40 }, // Project Name
      { wch: 20 }, // Work Type
      { wch: 30 }, // Fund Head
      { wch: 20 }, // Department
      { wch: 25 }, // Agency
      { wch: 12 }, // TS Date
      { wch: 15 }, // TS Amount
      { wch: 12 }, // WO Date
      { wch: 15 }, // WO No
      { wch: 15 }, // WO Amount
      { wch: 15 }, // Status
      { wch: 12 }, // Longitude
      { wch: 12 }  // Latitude
    ];
    ws['!cols'] = colWidths;
    
    // Style the header rows
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "14B8A6" } }, // Teal-500
      alignment: { horizontal: "center", vertical: "center" }
    };
    
    const titleStyle = {
      font: { bold: true, size: 14, color: { rgb: "0F172A" } },
      alignment: { horizontal: "left", vertical: "center" }
    };
    
    const subHeaderStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "3B82F6" } }, // Blue-500
      alignment: { horizontal: "center", vertical: "center" }
    };
    
    // Apply styles
    if (ws['A1']) ws['A1'].s = { font: { bold: true, size: 16 }, alignment: { horizontal: "center" } };
    if (ws['A2']) ws['A2'].s = { font: { size: 12 }, alignment: { horizontal: "center" } };
    if (ws['A4']) ws['A4'].s = titleStyle;
    
    // Style table headers (row 7) - Updated to include all columns including Agency
    ['A7', 'B7', 'C7', 'D7', 'E7', 'F7', 'G7', 'H7', 'I7', 'J7', 'K7', 'L7', 'M7', 'N7'].forEach(cell => {
      if (ws[cell]) ws[cell].s = headerStyle;
    });
    
    // Format TS Amount column (H - 8th column) as currency starting from row 8
    for (let i = 8; i <= 7 + uniqueProjects.length; i++) {
      const cellRef = `H${i}`;
      if (ws[cellRef]) {
        ws[cellRef].z = '₹#,##0';  // Indian Rupee format without decimals
      }
    }
    
    // Format WO Amount column (K - 11th column) as currency starting from row 8  
    for (let i = 8; i <= 7 + uniqueProjects.length; i++) {
      const cellRef = `K${i}`;
      if (ws[cellRef]) {
        ws[cellRef].z = '₹#,##0';  // Indian Rupee format without decimals
      }
    }
    
    // Color code status cells (L - 12th column)
    for (let i = 8; i <= 7 + uniqueProjects.length; i++) {
      const statusCell = `L${i}`;
      if (ws[statusCell]) {
        const status = ws[statusCell].v;
        switch (status) {
          case 'Completed':
            ws[statusCell].s = { fill: { fgColor: { rgb: "DCFCE7" } }, font: { color: { rgb: "15803D" } } };
            break;
          case 'Work Order':
            ws[statusCell].s = { fill: { fgColor: { rgb: "DBEAFE" } }, font: { color: { rgb: "1D4ED8" } } };
            break;
          case 'Proposed':
            ws[statusCell].s = { fill: { fgColor: { rgb: "FEF3C7" } }, font: { color: { rgb: "92400E" } } };
            break;
          default:
            ws[statusCell].s = { fill: { fgColor: { rgb: "F3F4F6" } }, font: { color: { rgb: "6B7280" } } };
            break;
        }
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Projects');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `${exportTitle.replace(/\s+/g, '_')}_Projects_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(data, fileName);
    
    console.log('Excel export completed successfully');
  } catch (error) {
    console.error('Excel export error:', error);
    alert('Error generating Excel file. Please try again.');
  }
};